// Note: for accurate profiling of query time we want to collect even fast responses
// HOWEVER: certain queries (such as RSDQs) make up the bulk of our queries so
// we'll apply a higher reporting threshold for those
import config from '../../../config';

export const MIN_NETWORK_ELAPSED_TIME_MILLIS = 50;
export const THROTTLED_MIN_NETWORK_ELAPSED_TIME_MILLIS = 1000;

// Requests to these domains will count towards the time-to-interactive metric.
export const TTI_DOMAIN_INCLUSIONS = [
  'wandb.com',
  'wandb.ai',
  'wandb.us',
  'wandb.test',
  'googleapis.com',
];

export const TTI_WANDB_ANALYTICS_DOMAIN = 'analytics.wandb.ai';

// Known third-party (ads/marketing/analytics/support) domains. Requests to these
// are ignored in the time-to-interactive metric and their RUM resource events are
// discarded in beforeSend (see datadogRumDiscardEvent.ts) so first-party requests
// stay readable in the resource explorer and session waterfall.
export const THIRD_PARTY_DOMAIN_EXCLUSIONS = [
  '6sc.co',
  '6sense.com',
  'adnxs.com',
  'ads-twitter.com',
  'bing.com',
  'bing.net',
  'browser-intake-datadoghq.com',
  'browser-intake-us5-datadoghq.com',
  'chameleon.io',
  'clarity.ms',
  'cookielaw.org',
  'datadoghq.com',
  'doubleclick.net',
  'gadstat.com',
  'getbeamer.com',
  'github.com',
  'google-analytics.com',
  'googleoptimize.com',
  'googlesyndication.com',
  'googletagmanager.com',
  'gravatar.com',
  'ip-api.com',
  'jquery.com',
  'jscloud.net',
  // Kaspersky antivirus injects its scripts into every page it scans.
  'kaspersky-labs.com',
  'licdn.com',
  'linkedin.com',
  'marketo.net',
  'mixpanel.com',
  'mktoresp.com',
  'mktoutil.com',
  'mxpnl.com',
  'onetrust.com',
  'oribi.io',
  'propensity-abm.com',
  'propensity.com',
  'qualified.com',
  'reddit.com',
  'redditstatic.com',
  'reo.dev',
  'segment.com',
  'segment.io',
  't.co',
  'twitter.com',
  // UserGems asset bucket; listed as the full host because s3.amazonaws.com
  // can't be excluded broadly (customer BYOB buckets live there too).
  'ug-webapp-public-production.s3.amazonaws.com',
  'usbrowserspeed.com',
  'usergems.com',
  'vector.co',
  'wordlift.io',
  'wp.com',
  // Matched as a prefix by isThirdPartyTrackerUrl to cover www.google.<tld>
  // ad/analytics pings across ~70 country TLDs.
  'www.google',
  'zdassets.com',
  'zendesk.com',
  'zoominfo.com',
  'zopim.com',
];

export const FRIENDLY_ENTRY_TYPE_NAMES = {
  // A long task is any uninterrupted period where the main UI thread is busy for 50ms or longer
  // note that longtask and event/first-input will overlap and report the same issue multiple times
  longtask: 'UI thread busy',
  // resource performance event occurs when a network request takes a long time to fulfill
  resource: 'Network',
  // event and first-input both measure how long it takes for the app to respond to input from the user
  // the value of 'duration' is the time from startTime to the next rendering paint.
  event: 'Slow UI response time',
  'first-input': 'First input delay',
} as const;

/*
How did we pick these limits?
* longtasks: we're finding that when the user is hovering over charts, it is getting
  marked as long tasks, but just above the 50ms threshold for longtasks, so we are just
  bumping up that threshold a bit.
* network: this is somewhat arbitrary, so it may need adjusting. The idea is that
  500ms is still "fast" from user's viewpoint for loading data. Hopefully we can
  make this value lower over time. This was picked by looking at network request in
  workspaces, and see what duration looked like for those call, and aiming for a
  ~p90 value.
*/
export const MINIMUM_NETWORK_DURATION_MS = 500;

export const MINIMUM_LONGTASK_DURATION_MS = 200;

export const PERF_LOG_PREFIX = 'WB_P'; // W&B Perf

// What performance events should we print to the console?
export const CONSOLE_REPORT_SLOW_EVENT_MILLIS = config.DATADOG_DEBUG ? 1 : 500;

// We don't report all events to server since we only want to capture data about
// particularly bad loading times
export const MIN_REPORT_TO_SERVER_TIME_MILLIS = config.DATADOG_DEBUG ? 1 : 500;

// This is data sent to DataDog as part of network performance events. We add this so it's
// easier to find network perf events, regardless of how they're reported to DD (graphql vs resource)
export const NETWORK_EVENT_CONTEXT = {isNetwork: true};
